import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Progress } from "@/components/Progress";
import BulletHole from "@/components/BulletHole";
import { Download, Copy, Mail, ArrowDown, ArrowUp, Minus, FileDown } from "lucide-react";

function arrow(delta) {
  if (delta > 0.02) return <ArrowUp size={11} className="text-[hsl(var(--signal-cyan))]" />;
  if (delta < -0.02) return <ArrowDown size={11} className="text-hud" />;
  return <Minus size={11} className="text-muted-foreground" />;
}

export default function Briefing() {
  const [b, setB] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/briefing").then((r) => setB(r.data));
  }, []);

  if (!b) {
    return (
      <div className="mono text-muted-foreground uppercase tracking-widest text-xs">
        Compiling intel…
      </div>
    );
  }

  const asText = () => {
    const L = [];
    L.push(`COMMAND CENTER — WEEKLY BRIEFING`);
    L.push(`${b.week_label}`);
    L.push(`Generated: ${new Date(b.generated_at).toLocaleString()}`);
    L.push(``);
    L.push(`VERDICT`);
    L.push(`  ${b.verdict}`);
    L.push(``);
    L.push(`HABITS · LAST 7 DAYS`);
    L.push(`  Overall: ${b.habits.last7_completed}/${b.habits.last7_total} (${Math.round(b.habits.last7_pct*100)}%)`);
    L.push(`  Top:`);
    b.habits.top.forEach(h => L.push(`    • ${h.name} — ${h.completed}/7 (${Math.round(h.pct*100)}%)`));
    if (b.habits.laggards.length) {
      L.push(`  Lagging:`);
      b.habits.laggards.forEach(h => L.push(`    • ${h.name} — ${h.completed}/7`));
    }
    L.push(``);
    L.push(`WEEK · OPERATING SYSTEM`);
    L.push(`  Current: ${b.weekly.current_done}/${b.weekly.current_total} (${Math.round(b.weekly.current_pct*100)}%)`);
    L.push(`  Previous: ${Math.round(b.weekly.previous_pct*100)}% · Delta: ${(b.weekly.delta>=0?"+":"")}${Math.round(b.weekly.delta*100)}%`);
    L.push(``);
    L.push(`PIPELINE`);
    L.push(`  Projected (open): $${Math.round(b.deals.projected_open).toLocaleString()}`);
    L.push(`  Active deals: ${b.deals.active_count}`);
    L.push(`  Closed this week: ${b.deals.closed_this_week_count} · $${Math.round(b.deals.closed_this_week_revenue).toLocaleString()}`);
    if (b.deals.top_open.length) {
      L.push(`  Top open deals:`);
      b.deals.top_open.forEach(d => L.push(`    • ${d.client} / ${d.property} — $${Math.round(d.projected).toLocaleString()} (${d.stage}, ${d.priority})`));
    }
    if (b.deals.followups_next7.length) {
      L.push(`  Follow-ups in next 7d:`);
      b.deals.followups_next7.forEach(f => L.push(`    • ${f.date} — ${f.client} (${f.priority})`));
    }
    L.push(``);
    L.push(`RE MEDIA`);
    L.push(`  Lifetime revenue: $${Math.round(b.media.revenue_total).toLocaleString()}`);
    L.push(`  Unpaid: ${b.media.unpaid_count} orders · $${Math.round(b.media.unpaid_value).toLocaleString()}`);
    if (b.media.unpaid_list.length) {
      L.push(`  Unpaid list:`);
      b.media.unpaid_list.forEach(m => L.push(`    • ${m.client} / ${m.property} — $${Math.round(m.total).toLocaleString()}`));
    }
    if (b.media.pending_shoots_next7.length) {
      L.push(`  Upcoming shoots (next 7d):`);
      b.media.pending_shoots_next7.forEach(s => L.push(`    • ${s.shoot_date} ${s.shoot_time || ""} — ${s.client} / ${s.property}`));
    }
    return L.join("\n");
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(asText());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([asText()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `briefing-${b.week_start}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="briefing">
      <header className="flex flex-wrap items-end justify-between gap-4 relative">
        <BulletHole variant={2} size={120} className="absolute -top-6 right-1/3 opacity-50" />
        <div className="relative z-[2]">
          <div className="kpi-label">Weekly brief</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            Weekly Brief
          </h1>
          <div className="mono text-[11px] text-muted-foreground tracking-widest uppercase mt-3">
            {b.week_label}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/dossier" className="hud-btn flex items-center gap-2" data-testid="open-dossier">
            <FileDown size={11} /> DOSSIER (PDF)
          </Link>
          <button onClick={copyText} className="hud-btn flex items-center gap-2" data-testid="copy-briefing">
            <Copy size={11} /> {copied ? "COPIED" : "COPY"}
          </button>
          <button onClick={download} className="hud-btn flex items-center gap-2" data-testid="download-briefing">
            <Download size={11} /> .TXT
          </button>
          <button
            onClick={() => alert("Auto-delivery requires an email provider (SendGrid or Resend). Drop credentials anytime and this becomes a scheduled Sunday 6PM send.")}
            className="hud-btn hud-btn-primary flex items-center gap-2"
            data-testid="schedule-briefing"
          >
            <Mail size={11} /> SCHEDULE SEND
          </button>
        </div>
      </header>

      {/* VERDICT */}
      <section className="hud-frame p-6" data-testid="verdict-block">
        <div className="kpi-label">Summary</div>
        <p className="display text-2xl sm:text-3xl mt-3 leading-snug font-semibold tracking-wide">
          {b.verdict}
        </p>
      </section>

      {/* HABITS + WEEK GRID */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="hud-frame p-6" data-testid="brief-habits">
          <div className="flex items-end justify-between">
            <div>
              <div className="kpi-label">HABITS · 7-DAY</div>
              <div className="kpi-value mt-2">{fmtPct(b.habits.last7_pct)}</div>
            </div>
            <div className="mono text-[11px] text-muted-foreground tracking-widest">
              {b.habits.last7_completed}/{b.habits.last7_total}
            </div>
          </div>
          <div className="mt-3"><Progress value={b.habits.last7_pct} label={false} /></div>

          <div className="hud-divider my-5" />
          <div className="space-y-3">
            <div className="kpi-label">TOP PERFORMERS</div>
            {b.habits.top.map((h) => (
              <div key={h.name} className="flex items-center gap-3 text-sm">
                <span className="flex-1 truncate">{h.name}</span>
                <div className="w-24"><Progress value={h.pct} label={false} /></div>
                <span className="mono text-xs text-muted-foreground w-8 text-right">
                  {h.completed}/7
                </span>
              </div>
            ))}
            {b.habits.laggards.length > 0 && (
              <>
                <div className="kpi-label mt-4 !text-[hsl(var(--signal-amber))] before:!content-['!__']">LAGGARDS</div>
                {b.habits.laggards.map((h) => (
                  <div key={h.name} className="flex items-center gap-3 text-sm">
                    <span className="flex-1 truncate">{h.name}</span>
                    <div className="w-24"><Progress value={h.pct} label={false} /></div>
                    <span className="mono text-xs text-muted-foreground w-8 text-right">
                      {h.completed}/7
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="hud-frame p-6" data-testid="brief-week">
          <div className="flex items-end justify-between">
            <div>
              <div className="kpi-label">WEEKLY OS · CURRENT</div>
              <div className="kpi-value mt-2">{fmtPct(b.weekly.current_pct)}</div>
            </div>
            <div className="text-right">
              <div className="mono text-[11px] text-muted-foreground tracking-widest">
                {b.weekly.current_done}/{b.weekly.current_total}
              </div>
              <div className="mono text-[11px] mt-1 flex items-center justify-end gap-1 tracking-widest uppercase">
                {arrow(b.weekly.delta)}
                <span className={b.weekly.delta>=0?"text-[hsl(var(--signal-cyan))]":"text-hud"}>
                  {b.weekly.delta>=0?"+":""}{Math.round(b.weekly.delta*100)}%
                </span>
                <span className="text-muted-foreground">VS PRIOR</span>
              </div>
            </div>
          </div>
          <div className="mt-3"><Progress value={b.weekly.current_pct} label={false} /></div>
          <div className="hud-divider my-5" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="kpi-label">THIS WEEK</div>
              <div className="display text-xl font-semibold mt-1">
                {fmtPct(b.weekly.current_pct)}
              </div>
            </div>
            <div>
              <div className="kpi-label">PRIOR WEEK</div>
              <div className="display text-xl font-semibold mt-1">
                {fmtPct(b.weekly.previous_pct)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEALS */}
      <section className="hud-frame p-6" data-testid="brief-deals">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="kpi-label">PIPELINE · OPEN POSITIONS</div>
            <div className="kpi-value mt-2">{fmtMoney(b.deals.projected_open)}</div>
            <div className="mono text-[11px] text-muted-foreground tracking-widest mt-1 uppercase">
              {b.deals.active_count} ACTIVE · {b.deals.closed_this_week_count} CLOSED THIS WEEK ·{" "}
              {fmtMoney(b.deals.closed_this_week_revenue)} BOOKED
            </div>
          </div>
        </div>

        {b.deals.top_open.length > 0 && (
          <div className="overflow-x-auto">
            <table className="ox-table">
              <thead>
                <tr>
                  <th>CLIENT</th>
                  <th>PROPERTY</th>
                  <th>STAGE</th>
                  <th>PRIORITY</th>
                  <th>EXP. CLOSE</th>
                  <th className="!text-right">PROJECTED</th>
                </tr>
              </thead>
              <tbody>
                {b.deals.top_open.map((d, i) => (
                  <tr key={i}>
                    <td>{d.client}</td>
                    <td>{d.property}</td>
                    <td><span className="hud-chip">{d.stage}</span></td>
                    <td>
                      <span className={`hud-chip ${d.priority==="High"?"hud-chip-on":""}`}>
                        {d.priority}
                      </span>
                    </td>
                    <td className="mono text-xs">{d.expected_close || "—"}</td>
                    <td className="num text-hud font-semibold">{fmtMoney(d.projected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {b.deals.followups_next7.length > 0 && (
          <>
            <div className="kpi-label mt-5">FOLLOW-UPS · NEXT 7 DAYS</div>
            <ul className="mt-2 space-y-1.5">
              {b.deals.followups_next7.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="mono text-xs text-muted-foreground w-24">{f.date}</span>
                  <span className="flex-1 truncate">{f.client}</span>
                  <span className={`hud-chip ${f.priority==="High"?"hud-chip-on":""}`}>{f.priority}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {b.deals.top_open.length === 0 && b.deals.followups_next7.length === 0 && (
          <div className="text-muted-foreground text-sm">No active deals on the board.</div>
        )}
      </section>

      {/* MEDIA */}
      <section className="hud-frame p-6" data-testid="brief-media">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
          <div>
            <div className="kpi-label">MEDIA · REVENUE</div>
            <div className="kpi-value mt-2">{fmtMoney(b.media.revenue_total)}</div>
          </div>
          <div>
            <div className="kpi-label">MEDIA · AR UNPAID</div>
            <div className="kpi-value mt-2 text-hud">
              {fmtMoney(b.media.unpaid_value)}
            </div>
            <div className="mono text-[11px] text-muted-foreground mt-1 uppercase tracking-widest">
              {b.media.unpaid_count} ORDERS
            </div>
          </div>
          <div>
            <div className="kpi-label">SHOOTS · NEXT 7D</div>
            <div className="kpi-value mt-2">
              {b.media.pending_shoots_next7.length}
            </div>
          </div>
        </div>

        {b.media.unpaid_list.length > 0 && (
          <>
            <div className="kpi-label">UNPAID LEDGER</div>
            <ul className="mt-2 space-y-1.5">
              {b.media.unpaid_list.map((m, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="flex-1 truncate">
                    {m.client} <span className="text-muted-foreground">/ {m.property}</span>
                  </span>
                  <span className="mono text-xs text-muted-foreground">
                    {m.shoot_date || "—"}
                  </span>
                  <span className="num font-semibold text-hud w-24">{fmtMoney(m.total)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {b.media.pending_shoots_next7.length > 0 && (
          <>
            <div className="kpi-label mt-5">UPCOMING SHOOTS</div>
            <ul className="mt-2 space-y-1.5">
              {b.media.pending_shoots_next7.map((s, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="mono text-xs text-muted-foreground w-24">
                    {s.shoot_date} {s.shoot_time || ""}
                  </span>
                  <span className="flex-1 truncate">
                    {s.client} <span className="text-muted-foreground">/ {s.property}</span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <div className="mono text-[10px] text-muted-foreground uppercase tracking-[0.25em] text-center pb-4">
        COMMAND CENTER · FOR CEO'S ONLY
      </div>
    </div>
  );
}
